import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  HStack,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  FundraisingDocumentType,
  useGetDocumentPdfUrlQuery,
  useGetSubscriptionFundraisingDocumentsQuery,
} from '@blockpulse3/graphql/hooks';
import {
  formatNumberCurrency,
  formatNumberPercentage,
  formatReferenceLabel,
} from '@blockpulse3/helpers';
import {
  BankDetailsCard,
  BankDetailsCardDocument,
  ErrorQueryModal,
  ResponsiveModal,
  ResponsiveModalFooter,
  ResponsiveModalProps,
  SkeletonModal,
} from '@blockpulse3/ui/commons';

type Props = {
  onClose: () => void;
} & Omit<ResponsiveModalProps, 'children'>;

/**
 * SubscriptionBankDetailsModal.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function SubscriptionBankDetailsModal({ onClose, ...props }: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nSubscriptionBankDetailsModal = useTranslations('SubscriptionBankDetailsModal');

  const { subscriptionId = '' } = useParams();

  const { data, loading, error } = useGetSubscriptionFundraisingDocumentsQuery({
    variables: { subscriptionId },
  });
  const bankDetailsDocument = data?.subscription?.fundraising?.documents?.find(
    (document) => document.type === FundraisingDocumentType.IBAN,
  );

  const { data: bankDetailsDocumentData } = useGetDocumentPdfUrlQuery({
    variables: {
      documentId: bankDetailsDocument?.document.id || '',
    },
    skip: !bankDetailsDocument?.document?.id,
  });

  if (loading) {
    return (
      <ResponsiveModal onClose={onClose} {...props}>
        <ModalOverlay />
        <SkeletonModal />
      </ResponsiveModal>
    );
  }

  if (error) {
    return (
      <ResponsiveModal onClose={onClose} {...props}>
        <ModalOverlay />
        <ErrorQueryModal />
      </ResponsiveModal>
    );
  }

  if (!data) {
    return (
      <ResponsiveModal onClose={onClose} {...props}>
        <ModalOverlay />
        <ErrorQueryModal />
      </ResponsiveModal>
    );
  }

  const { subscription } = data;
  const { admissionFees = 0, feesAmount = 0, amountDue = 0, investAmount = 0 } = subscription;

  const virtualIBAN = subscription?.bankVirtualIban;
  const bankDetailsTitle = `${i18nSubscriptionBankDetailsModal('ModalCardTitle')} ${
    subscription.fundraising.company.name
  }`;
  const bankDetailsDocumentLink = bankDetailsDocumentData?.getDocumentPdfUrl;

  const referenceLabel = subscription.reference
    ? formatReferenceLabel(subscription.identity.name, subscription.reference.label)
    : '';

  return (
    <ResponsiveModal isCentered onClose={onClose} {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{i18nSubscriptionBankDetailsModal('ModalTitle')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody as={Stack} spacing="4">
          <Text>
            {bankDetailsDocument
              ? i18nSubscriptionBankDetailsModal('ModalSubtitleFixed')
              : i18nSubscriptionBankDetailsModal.rich('ModalSubtitle', {
                  important: (chunk) => <b>{chunk}</b>,
                })}
          </Text>
          <Stack spacing="4">
            {bankDetailsDocumentData ? (
              <BankDetailsCardDocument
                referenceLabel={referenceLabel}
                src={bankDetailsDocumentLink}
                title={bankDetailsTitle}
              />
            ) : (
              <BankDetailsCard
                iban={virtualIBAN?.iban}
                recipientName={virtualIBAN?.bankAccount.holderName}
                referenceLabel={referenceLabel}
                swift={virtualIBAN?.swift}
                title={bankDetailsTitle}
              />
            )}
            <Card bg="gray.50" boxShadow="none">
              <CardHeader as={Stack}>
                <Text fontSize="md" fontWeight="600">
                  {i18nSubscriptionBankDetailsModal('SubscriptionFundsTitle')}
                </Text>
                <HStack justify="space-between">
                  <Text color="gray.500" fontSize="sm" fontWeight="600">
                    {i18nSubscriptionBankDetailsModal('SubscriptionFundsShares')}
                  </Text>
                  <Text fontSize="sm" fontWeight="600">
                    {formatNumberCurrency(investAmount)}
                  </Text>
                </HStack>
                {admissionFees && (
                  <HStack justify="space-between">
                    <Text color="gray.500" fontSize="sm" fontWeight="600">
                      {i18nSubscriptionBankDetailsModal('SubscriptionFundsFees', {
                        feePercentage: formatNumberPercentage(admissionFees),
                      })}
                    </Text>
                    <Text fontSize="sm" fontWeight="600">
                      {formatNumberCurrency(feesAmount)}
                    </Text>
                  </HStack>
                )}
              </CardHeader>
              <CardBody as={Stack} spacing="2">
                <Divider />
                <HStack justifyContent="space-between" pt="1">
                  <Text fontSize="md" fontWeight="600">
                    {i18nSubscriptionBankDetailsModal('SubscriptionFundsTotal')}
                  </Text>
                  <Text fontSize="md" fontWeight="600">
                    {formatNumberCurrency(amountDue)}
                  </Text>
                </HStack>
              </CardBody>
            </Card>
          </Stack>
        </ModalBody>
        <Divider />
        <ResponsiveModalFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            {i18nGeneral('Close')}
          </Button>
        </ResponsiveModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
}

export type SubscriptionBankDetailsModalProps = Props;
